'use client';

import React, { useEffect, useRef } from 'react';
import { useNinetailed } from '@ninetailed/experience.js-react';
import { Reference } from '@ninetailed/experience.js-shared';
import { ComponentMarker } from 'components/shared/Ninetailed/ComponentMarker';
import { ExtendedExperienceConfiguration } from 'libs/contentful/queries/ninetailed';

/**
 * Pulled from https://github.com/ninetailed-inc/ninetailed-examples/blob/main/marketing-contentful-next-app-all-in/components/Client/ComponentTracker.tsx#
 */
export function ComponentTracker<Variant extends Reference>({
  experience,
  variant,
  variantIndex,
}: {
  experience?: ExtendedExperienceConfiguration<Variant>;
  variant: Variant;
  variantIndex: number;
}) {
  const componentRef = useRef<Element | null>(null);
  const ninetailed = useNinetailed();

  useEffect(() => {
    const componentElement = componentRef.current;

    if (componentElement && !(componentElement instanceof Element)) {
      const isObject = typeof componentElement === 'object' && componentElement !== null;
      const constructorName = isObject
        ? // eslint-disable-next-line
          ((componentElement as any).constructor.name as string)
        : '';
      const isConstructorNameNotObject = constructorName && constructorName !== 'Object';

      console.warn(
        `The component ref being in Experience is an invalid element. Expected an Element but got ${typeof componentElement}${
          isConstructorNameNotObject ? ` of type ${constructorName}` : ''
        }. This component won't be observed.`,
      );

      return () => {
        // noop
      };
    }

    if (componentElement) {
      ninetailed.observeElement({
        element: componentElement,
        experience,
        audience: experience?.audience,
        variant: 'hidden' in variant && variant.hidden ? { ...variant, id: `${variant.id}-hidden` } : variant,
        variantIndex,
        componentType: 'Entry',
      });

      return () => {
        if (componentElement) {
          ninetailed.unobserveElement(componentElement);
        }
      };
    }

    return () => {
      // noop
    };
  }, [experience, variant, variantIndex, ninetailed]);

  return <ComponentMarker ref={componentRef}></ComponentMarker>;
}
